/**
 * ListPagination Component
 *
 * A reusable pagination component that supports both "Load More" style
 * and full pagination controls with page numbers. Responsive design
 * for both mobile and desktop views.
 *
 * @example
 * ```tsx
 * // Load More style (infinite scroll pattern)
 * <ListPagination
 *   mode="loadMore"
 *   hasMore={hasMoreItems}
 *   onLoadMore={handleLoadMore}
 *   loading={loadingMore}
 * />
 *
 * // Full pagination
 * <ListPagination
 *   mode="pagination"
 *   currentPage={currentPage}
 *   totalPages={totalPages}
 *   totalCount={totalCount}
 *   pageSize={pageSize}
 *   onPageChange={handlePageChange}
 * />
 * ```
 */
import React from 'react';
import { Loader, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';

export interface LoadMoreProps {
  mode: 'loadMore';
  /** Whether there are more items to load */
  hasMore: boolean;
  /** Callback when "Load More" is clicked */
  onLoadMore: () => void;
  /** Whether more items are currently loading */
  loading?: boolean;
  /** Label for the load more button (default: "Load More") */
  loadMoreLabel?: string;
  /** Label while loading (default: "Loading...") */
  loadingLabel?: string;
}

export interface PaginationProps {
  mode: 'pagination';
  /** Current page number (1-indexed) */
  currentPage: number;
  /** Total number of pages */
  totalPages: number;
  /** Total count of items (optional, for display) */
  totalCount?: number;
  /** Number of items per page (optional, for display) */
  pageSize?: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Whether pagination controls are disabled */
  loading?: boolean;
  /** Whether to show first/last page buttons (default: true on desktop) */
  showFirstLast?: boolean;
  /** Whether to show page info text (default: true) */
  showPageInfo?: boolean;
  /** Whether to keep controls visible when there is only one page */
  showWhenSinglePage?: boolean;
}

export type ListPaginationProps = (LoadMoreProps | PaginationProps) & {
  /** Optional CSS class for the container */
  className?: string;
};

const ListPagination: React.FC<ListPaginationProps> = (props) => {
  const { className = '' } = props;

  // Load More mode
  if (props.mode === 'loadMore') {
    const {
      hasMore,
      onLoadMore,
      loading = false,
      loadMoreLabel = 'Load More',
      loadingLabel = 'Loading...',
    } = props;

    if (!hasMore) return null;

    return (
      <div className={`mt-4 flex justify-center ${className}`}>
        <button
          onClick={onLoadMore}
          disabled={loading}
          className="px-6 py-2 bg-slate-200 hover:bg-slate-300 text-slate-800 rounded-md text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <span className="flex items-center gap-2">
              <Loader size={16} className="animate-spin" />
              {loadingLabel}
            </span>
          ) : (
            loadMoreLabel
          )}
        </button>
      </div>
    );
  }

  // Pagination mode
  const {
    currentPage,
    totalPages,
    totalCount,
    pageSize,
    onPageChange,
    loading = false,
    showFirstLast = true,
    showPageInfo = true,
    showWhenSinglePage = false,
  } = props;

  if (totalPages <= 1 && !showWhenSinglePage) return null;

  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  // Calculate display range
  const startItem = pageSize ? (currentPage - 1) * pageSize + 1 : null;
  const endItem = pageSize && totalCount ? Math.min(currentPage * pageSize, totalCount) : null;

  return (
    <div className={`mt-4 ${className}`}>
      {/* Desktop Pagination */}
      <div className="hidden sm:flex items-center justify-between bg-white p-3 border border-gray-200 rounded-lg shadow-sm">
        {/* Page Info */}
        {showPageInfo && (
          <div className="flex items-center text-sm text-gray-700">
            {totalCount !== undefined && startItem && endItem ? (
              <span>
                Showing {startItem}-{endItem} of {totalCount}
              </span>
            ) : (
              <span>
                Page {currentPage} of {totalPages}
              </span>
            )}
          </div>
        )}

        {/* Navigation Controls */}
        <div className="flex items-center space-x-2">
          {/* First Page */}
          {showFirstLast && (
            <button
              onClick={() => onPageChange(1)}
              disabled={!canGoPrevious || loading}
              className="p-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="First page"
            >
              <ChevronsLeft size={16} />
            </button>
          )}

          {/* Previous */}
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!canGoPrevious || loading}
            className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            <ChevronLeft size={16} className="mr-1" />
            Previous
          </button>

          {/* Page Numbers */}
          <div className="flex items-center space-x-1">
            {generatePageNumbers(currentPage, totalPages).map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} className="px-2 py-1 text-gray-400">
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => onPageChange(page as number)}
                  disabled={loading || page === currentPage}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    page === currentPage
                      ? 'bg-slate-800 text-white'
                      : 'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  } disabled:cursor-not-allowed`}
                >
                  {page}
                </button>
              )
            ))}
          </div>

          {/* Next */}
          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!canGoNext || loading}
            className="px-3 py-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            Next
            <ChevronRight size={16} className="ml-1" />
          </button>

          {/* Last Page */}
          {showFirstLast && (
            <button
              onClick={() => onPageChange(totalPages)}
              disabled={!canGoNext || loading}
              className="p-2 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Last page"
            >
              <ChevronsRight size={16} />
            </button>
          )}
        </div>
      </div>

      {/* Mobile Pagination */}
      <div className="sm:hidden flex flex-col space-y-3">
        {/* Page Info */}
        {showPageInfo && (
          <div className="text-center text-sm text-gray-600">
            {totalCount !== undefined && startItem && endItem ? (
              <span>
                {startItem}-{endItem} of {totalCount}
              </span>
            ) : (
              <span>
                Page {currentPage} of {totalPages}
              </span>
            )}
          </div>
        )}

        {/* Navigation Controls */}
        <div className="flex justify-between space-x-2">
          <button
            onClick={() => onPageChange(currentPage - 1)}
            disabled={!canGoPrevious || loading}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-h-[44px]"
          >
            <ChevronLeft size={18} className="mr-1" />
            Previous
          </button>

          <button
            onClick={() => onPageChange(currentPage + 1)}
            disabled={!canGoNext || loading}
            className="flex-1 px-4 py-3 border border-gray-300 rounded-md bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center min-h-[44px]"
          >
            Next
            <ChevronRight size={18} className="ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
};

/**
 * Generate page numbers with ellipsis for large page counts
 */
function generatePageNumbers(currentPage: number, totalPages: number): (number | string)[] {
  const pages: (number | string)[] = [];
  const maxVisiblePages = 5;

  if (totalPages <= maxVisiblePages + 2) {
    // Show all pages if there aren't many
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
  } else {
    // Always show first page
    pages.push(1);

    // Calculate middle range
    let startPage = Math.max(2, currentPage - 1);
    let endPage = Math.min(totalPages - 1, currentPage + 1);

    // Adjust if at the beginning
    if (currentPage <= 3) {
      startPage = 2;
      endPage = Math.min(totalPages - 1, 4);
    }

    // Adjust if at the end
    if (currentPage >= totalPages - 2) {
      startPage = Math.max(2, totalPages - 3);
      endPage = totalPages - 1;
    }

    // Add ellipsis before middle pages if needed
    if (startPage > 2) {
      pages.push('...');
    }

    // Add middle pages
    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    // Add ellipsis after middle pages if needed
    if (endPage < totalPages - 1) {
      pages.push('...');
    }

    // Always show last page
    pages.push(totalPages);
  }

  return pages;
}

export default ListPagination;
